import { BaseEntityType } from '../../../types/entities';

/**
 * Reference entity - created on-demand when citing a paper in DocsApp
 * These are lightweight citation records that reference the source PDF
 */
export interface ReferenceEntity extends BaseEntityType {
  entityType: 'Reference';
  skeleton: {
    '@type': 'Reference';
    
    // Core metadata
    title: string;
    authors: string[];
    year: number;
    
    // Publication info
    journal?: string;
    volume?: string;
    issue?: string;
    pages?: string;
    publisher?: string;
    
    // Identifiers
    doi?: string;
    pmid?: string;
    arxiv?: string;
    url?: string;
    
    // Content
    abstract?: string;
    keywords?: string[];
    
    // Organization
    tags: string[];
    
    // Attachments (PDF file entity IDs)
    primaryPdfId?: string;
    attachments: string[];
    
    // Generated
    citationKey: string; // "Smith2020"
    
    // Metadata
    addedAt: string;
    readStatus?: 'unread' | 'reading' | 'read';
  };
}

/**
 * LibraryPDF - PDF entity with enriched reference metadata
 * These are what LibraryApp displays and manages
 */
export interface LibraryPDF extends BaseEntityType {
  entityType: 'File.Document.PDF';
  skeleton: {
    '@type': 'PDF';
    fileName: string;
    fileId: string;
    
    // Reference metadata (from retrieve_pdf_metadata command)
    referenceMetadata?: {
      itemType: string;
      title: string;
      authors: Array<{
        firstName: string;
        lastName: string;
        fullName: string;
      }>;
      publication?: string;
      volume?: string;
      issue?: string;
      pages?: string;
      date?: string;  // e.g., "2025-12"
      DOI?: string;
      ISSN?: string;
      url?: string;
      abstractNote?: string;
      language?: string;
      source?: string;  // "crossref" | "openalex"
      confidence?: number;
      retrievedAt?: string;
    };
    
    // Enrichment data
    enrichedData?: any;
    enrichmentStatus?: string;
    
    // Link to Reference entity (if cited)
    linkedReferenceId?: string;
  };
}

/**
 * Extract year from LibraryPDF metadata
 */
export function extractYear(pdf: LibraryPDF): number {
  const dateStr = pdf.skeleton.referenceMetadata?.date;
  if (dateStr) {
    const year = parseInt(dateStr.substring(0, 4));
    if (!isNaN(year)) return year;
  }
  return new Date().getFullYear();
}

/**
 * Get display title from LibraryPDF
 */
export function getDisplayTitle(pdf: LibraryPDF): string {
  const title = pdf.skeleton.referenceMetadata?.title 
    || pdf.skeleton.fileName 
    || pdf.name 
    || 'Untitled';
  
  console.log('[getDisplayTitle] Title extraction:', {
    hasMetadata: !!pdf.skeleton.referenceMetadata,
    metadataTitle: pdf.skeleton.referenceMetadata?.title,
    fileName: pdf.skeleton.fileName,
    pdfName: pdf.name,
    selectedTitle: title
  });
  
  return title;
}

/**
 * Get author string array from LibraryPDF
 */
export function getAuthorStrings(pdf: LibraryPDF): string[] {
  const metadataAuthors = pdf.skeleton.referenceMetadata?.authors;
  
  if (metadataAuthors && Array.isArray(metadataAuthors) && metadataAuthors.length > 0) {
    // Extract fullName if it exists, otherwise use the author object as string
    return metadataAuthors.map((a: any) => {
      if (typeof a === 'string') return a;
      if (a.fullName) return a.fullName;
      if (a.name) return a.name;
      // Construct from firstName + lastName
      if (a.firstName && a.lastName) return `${a.firstName} ${a.lastName}`;
      return 'Unknown';
    }).filter(name => name !== 'Unknown');
  }
  
  // Fallback: Try to extract from enrichedData
  if (pdf.skeleton.enrichedData?.authors && Array.isArray(pdf.skeleton.enrichedData.authors)) {
    return pdf.skeleton.enrichedData.authors.map((a: any) => 
      typeof a === 'string' ? a : (a.name || a.fullName || 'Unknown')
    );
  }
  
  return [];
}

/**
 * Generate citation key from authors and year
 * Example: ["Smith, J.", "Jones, M."], 2020 -> "Smith2020"
 */
export function generateCitationKey(authors: string[], year: number): string {
  if (!authors || authors.length === 0) return `Unknown${year}`;
  
  // Extract last name from first author
  const firstAuthor = authors[0];
  const lastNameMatch = firstAuthor.match(/([A-Z][a-z]+)(?:,|\s|$)/);
  const lastName = lastNameMatch ? lastNameMatch[1] : firstAuthor.split(' ').pop() || 'Unknown';
  
  return `${lastName}${year}`;
}

/**
 * Format authors for display
 * Example: ["Smith, J.", "Jones, M.", "Brown, K."] -> "Smith, Jones, & Brown"
 */
export function formatAuthors(authors: string[], maxAuthors: number = 3): string {
  if (!authors || authors.length === 0) return 'Unknown';
  
  const lastNames = authors.map(author => {
    const match = author.match(/([A-Z][a-z]+)(?:,|\s|$)/);
    return match ? match[1] : author.split(' ').pop() || 'Unknown';
  });
  
  if (lastNames.length === 1) return lastNames[0];
  if (lastNames.length === 2) return `${lastNames[0]} & ${lastNames[1]}`;
  if (lastNames.length <= maxAuthors) {
    return lastNames.slice(0, -1).join(', ') + ' & ' + lastNames[lastNames.length - 1];
  }
  
  return `${lastNames[0]} et al.`;
}

/**
 * Create Reference entity from LibraryPDF (for citation insertion)
 */
export function createReferenceFromPDF(pdf: LibraryPDF, spaceId: string): any {
  const metadata = pdf.skeleton.referenceMetadata;
  const title = getDisplayTitle(pdf);
  const authors = getAuthorStrings(pdf);
  const year = extractYear(pdf);
  
  return {
    entityType: 'Reference',
    name: title,
    spaceId,
    skeleton: {
      '@type': 'Reference',
      title,
      authors,
      year,
      journal: metadata?.publication,
      volume: metadata?.volume,
      issue: metadata?.issue,
      pages: metadata?.pages,
      doi: metadata?.DOI,
      url: metadata?.url,
      abstract: metadata?.abstractNote,
      tags: [],
      primaryPdfId: pdf._id,
      attachments: [pdf._id],
      citationKey: generateCitationKey(authors, year),
      addedAt: new Date().toISOString(),
      readStatus: 'unread'
    }
  };
}

